import { useEffect, useRef, useCallback } from 'react'

interface PerformanceMetrics {
  messageHandlerTime: number
  violationCount: number
  lastViolation: number
}

interface UsePerformanceMonitorOptions {
  onViolation?: (metrics: PerformanceMetrics) => void
  threshold?: number // milliseconds
  debounceMs?: number
}

// Global state to track if monitoring has been initialized
let globalMonitoringInitialized = false

export const usePerformanceMonitor = (options: UsePerformanceMonitorOptions = {}) => {
  const { onViolation, threshold = 50, debounceMs = 100 } = options
  const metricsRef = useRef<PerformanceMetrics>({
    messageHandlerTime: 0,
    violationCount: 0,
    lastViolation: 0
  })
  const timeoutRef = useRef<NodeJS.Timeout>()

  const reportViolation = useCallback((time: number) => {
    metricsRef.current = {
      messageHandlerTime: time,
      violationCount: metricsRef.current.violationCount + 1,
      lastViolation: Date.now()
    }

    // Debounce violation reports
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      if (onViolation) {
        onViolation(metricsRef.current)
      }
    }, debounceMs)
  }, [onViolation, debounceMs])

  // Monitor message handler performance
  useEffect(() => {
    // Performance monitoring disabled due to TypeScript compatibility issues
    // Only log in development and only once globally
    if (process.env.NODE_ENV === 'development' && !globalMonitoringInitialized) {
      console.log('Performance monitoring initialized (disabled)')
      globalMonitoringInitialized = true
    }
    
    return () => {
      // No cleanup logging to reduce spam
    }
  }, []) // No dependencies to prevent re-initialization

  // Performance optimization utilities
  const debounce = useCallback(<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): T => {
    let timeoutId: NodeJS.Timeout
    return ((...args: any[]) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => func(...args), delay)
    }) as T
  }, [])

  const throttle = useCallback(<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): T => {
    let inThrottle: boolean
    return ((...args: any[]) => {
      if (!inThrottle) {
        func(...args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }) as T
  }, [])

  return {
    metrics: metricsRef.current,
    debounce,
    throttle,
    reportViolation
  }
}

// Hook specifically for Stripe performance monitoring
export const useStripePerformanceMonitor = () => {
  const { metrics, reportViolation } = usePerformanceMonitor({
    threshold: 50, // 50ms threshold for Stripe operations
    onViolation: (metrics) => {
      if (process.env.NODE_ENV === 'development') {
        // Only log violations that are significantly slow (>100ms) to reduce spam
        if (metrics.messageHandlerTime > 100) {
          console.warn('Stripe performance violation detected:', {
            duration: `${metrics.messageHandlerTime.toFixed(2)}ms`,
            violationCount: metrics.violationCount,
            timeSinceLastViolation: Date.now() - metrics.lastViolation
          })
        }
      }
    }
  })

  return {
    metrics,
    reportViolation
  }
}
